import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { api, API, formatApiErrorDetail } from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { SubcontractorsPanel } from "@/pages/SubcontractorsPanel";
import { MonthCalendar } from "@/components/MonthCalendar";
import {
  HammerIcon, SignOutIcon, TrayIcon, CalendarCheckIcon,
  WrenchIcon, CheckCircleIcon, PhoneIcon, EnvelopeSimpleIcon,
  MapPinIcon, ClockIcon, ImageIcon, CalendarBlankIcon,
  CurrencyDollarIcon, RowsIcon, CalendarDotsIcon,
  UploadSimpleIcon, ImagesIcon, GlobeIcon,
  ReceiptIcon, StarIcon, PlusIcon, TrashIcon, UsersThreeIcon, CalendarPlusIcon, ArrowsClockwiseIcon,
} from "@phosphor-icons/react";

const STATUSES = ["new", "scheduled", "in-progress", "completed"];
const CATEGORIES = ["Plumbing", "Electrical", "Carpentry", "Painting", "Drywall", "HVAC", "Roofing", "Flooring", "Masonry"];

const STATUS_STYLE = {
  new: "bg-slate-200 text-slate-800",
  scheduled: "bg-gold-100 text-gold-800",
  "in-progress": "bg-blue-100 text-blue-800",
  completed: "bg-green-100 text-green-800",
};

const STAT_META = [
  { key: "total", label: "Total", icon: TrayIcon, color: "text-slate-900" },
  { key: "new", label: "New", icon: ClockIcon, color: "text-slate-700" },
  { key: "scheduled", label: "Scheduled", icon: CalendarCheckIcon, color: "text-gold-600" },
  { key: "in-progress", label: "In Progress", icon: WrenchIcon, color: "text-blue-600" },
  { key: "completed", label: "Completed", icon: CheckCircleIcon, color: "text-green-600" },
];

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [stats, setStats] = useState({});
  const [statusFilter, setStatusFilter] = useState("all");
  const [calStatusFilter, setCalStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [noteDraft, setNoteDraft] = useState("");
  const [scheduleDraft, setScheduleDraft] = useState("");
  const [estimateDraft, setEstimateDraft] = useState("");
  const [completionDraft, setCompletionDraft] = useState([]);
  const [completionUploading, setCompletionUploading] = useState(false);
  const [invoiceItems, setInvoiceItems] = useState([]);
  const [depositPct, setDepositPct] = useState(50);
  const [view, setView] = useState("list");
  const [selectedDay, setSelectedDay] = useState(null);
  const [timezone, setTimezone] = useState("");
  const [tzList, setTzList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviews, setReviews] = useState([]);
  const [subs, setSubs] = useState([]);
  const [assignSubId, setAssignSubId] = useState("");
  const [assignPay, setAssignPay] = useState("");
  const [replyDrafts, setReplyDrafts] = useState({});
  const [showcaseCaption, setShowcaseCaption] = useState("");
  useEffect(() => { setShowcaseCaption(selected?.showcase_caption || ""); }, [selected?.id]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, s, cfg, rv] = await Promise.all([
        api.get("/admin/requests", { params: { status: statusFilter, category: categoryFilter } }),
        api.get("/admin/stats"),
        api.get("/admin/settings"),
        api.get("/admin/reviews"),
      ]);
      setRequests(r.data);
      setStats(s.data);
      setTimezone(cfg.data.timezone);
      setTzList(cfg.data.available_timezones || []);
      setReviews(rv.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [statusFilter, categoryFilter]);

  useEffect(() => { load(); }, [load]);

  const doLogout = async () => {
    await logout();
    navigate("/admin/login");
  };

  const openDetail = (req) => {
    setSelected(req);
    setNoteDraft(req.admin_note || "");
    setScheduleDraft(req.scheduled_date || "");
    setEstimateDraft(req.estimate || "");
    setCompletionDraft(req.completion_photos || []);
    const inv = req.invoice;
    setInvoiceItems(inv?.line_items?.length ? inv.line_items.map((i) => ({ ...i })) : [{ description: "", qty: 1, unit_price: 0 }]);
    setDepositPct(inv?.deposit_pct ?? 50);
  };

  const promptRescheduleReason = () => {
    const changed = scheduleDraft && selected?.scheduled_date && scheduleDraft !== selected.scheduled_date;
    if (!changed) return { proceed: true, reason: undefined };
    const reason = window.prompt(`You're moving ${selected.ref} to ${scheduleDraft}.\n\nOptional note for the customer explaining why (leave blank to skip):`);
    if (reason === null) return { proceed: false, reason: undefined };
    return { proceed: true, reason };
  };

  const updateStatus = async (newStatus) => {
    const { proceed, reason } = promptRescheduleReason();
    if (!proceed) return;
    try {
      const res = await api.patch(`/admin/requests/${selected.id}`, { status: newStatus, admin_note: noteDraft, scheduled_date: scheduleDraft, estimate: estimateDraft, reschedule_reason: reason });
      setSelected(res.data);
      toast.success(`Marked ${newStatus}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Update failed");
    }
  };

  const saveDetails = async () => {
    const { proceed, reason } = promptRescheduleReason();
    if (!proceed) return;
    try {
      const res = await api.patch(`/admin/requests/${selected.id}`, { status: selected.status, admin_note: noteDraft, scheduled_date: scheduleDraft, estimate: estimateDraft, reschedule_reason: reason });
      setSelected(res.data);
      toast.success("Saved");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    }
  };

  const changeTimezone = async (tz) => {
    setTimezone(tz);
    try {
      await api.patch("/admin/settings", { timezone: tz });
      toast.success("Reminder timezone updated");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save timezone");
    }
  };

  const persistCompletion = async (photos) => {
    const res = await api.patch(`/admin/requests/${selected.id}`, { status: selected.status, completion_photos: photos });
    setSelected(res.data);
    setCompletionDraft(res.data.completion_photos || []);
    load();
  };

  const sendReviewRequest = async () => {
    try {
      const res = await api.post(`/admin/requests/${selected.id}/send-review-request`);
      setSelected(res.data);
      load();
      toast.success("Review request sent to the customer");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not send review request");
    }
  };

  const rescheduleJob = async (job, newDate) => {
    if (!job || !newDate || job.date === newDate) return;
    const reason = window.prompt(`Reschedule ${job.ref} to ${newDate}?\n\nOptional note for the customer explaining why (leave blank to skip):`);
    if (reason === null) return;
    try {
      await api.patch(`/admin/requests/${job.id}`, { status: job.status, scheduled_date: newDate, reschedule_reason: reason });
      toast.success(`${job.ref} moved to ${newDate}`);
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not reschedule");
    }
  };

  const toggleShowcase = async (featured) => {
    try {
      const res = await api.patch(`/admin/requests/${selected.id}/feature-showcase`, { featured, caption: showcaseCaption });
      setSelected(res.data);
      load();
      toast.success(featured ? "Saved to homepage" : "Removed from homepage");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not update showcase");
    }
  };

  const uploadCompletion = async (files) => {
    if (!files || files.length === 0) return;
    setCompletionUploading(true);
    try {
      const uploaded = [];
      for (const f of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", f);
        const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        uploaded.push(r.data.path);
      }
      await persistCompletion([...(completionDraft || []), ...uploaded]);
      toast.success(`${uploaded.length} photo(s) added`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally {
      setCompletionUploading(false);
    }
  };

  const removeCompletion = async (p) => {
    try {
      await persistCompletion(completionDraft.filter((x) => x !== p));
    } catch (e) {
      toast.error("Failed to remove");
    }
  };

  const invoiceTotal = invoiceItems.reduce((s, i) => s + (parseFloat(i.qty) || 0) * (parseFloat(i.unit_price) || 0), 0);
  const updateItem = (idx, field, val) => setInvoiceItems((items) => items.map((it, i) => (i === idx ? { ...it, [field]: val } : it)));
  const addItem = () => setInvoiceItems((items) => [...items, { description: "", qty: 1, unit_price: 0 }]);
  const removeItem = (idx) => setInvoiceItems((items) => items.filter((_, i) => i !== idx));
  const saveInvoice = async () => {
    const items = invoiceItems
      .filter((i) => (i.description || "").trim())
      .map((i) => ({ description: i.description, qty: parseFloat(i.qty) || 0, unit_price: parseFloat(i.unit_price) || 0 }));
    if (!items.length) {
      toast.error("Add at least one line item");
      return;
    }
    try {
      const res = await api.post(`/admin/requests/${selected.id}/invoice`, { line_items: items, deposit_pct: parseFloat(depositPct) || 0 });
      setSelected(res.data);
      toast.success("Invoice saved");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    }
  };

  const reviewPhoto = async (path, action) => {
    let reason = "";
    if (action === "reject") {
      reason = window.prompt("Add a note so the subcontractor knows what to redo (optional):") || "";
    }
    try {
      const res = await api.post(`/admin/requests/${selected.id}/photos/review`, { path, action, reason });
      setSelected(res.data);
      setCompletionDraft(res.data.completion_photos || []);
      load();
      toast.success(action === "approve" ? "Photo approved & published" : "Photo rejected");
    } catch (e) {
      toast.error("Failed to update photo");
    }
  };

  const scheduledJobs = requests
    .filter((r) => r.scheduled_date)
    .sort((a, b) => a.scheduled_date.localeCompare(b.scheduled_date));

  const toggleFeature = async (r) => {
    try {
      const res = await api.patch(`/admin/requests/${r.id}/feature-review`, { featured: !r.review?.featured });
      setReviews((list) => list.map((x) => (x.id === r.id ? res.data : x)));
      toast.success(res.data.review?.featured ? "Featured on public page" : "Removed from public page");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to update");
    }
  };
  const saveReply = async (r) => {
    try {
      const res = await api.patch(`/admin/requests/${r.id}/review-reply`, { reply: replyDrafts[r.id] ?? r.review?.reply ?? "" });
      setReviews((list) => list.map((x) => (x.id === r.id ? res.data : x)));
      toast.success("Reply saved");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save reply");
    }
  };
  const avgRating = reviews.length
    ? (reviews.reduce((s, r) => s + (r.review?.rating || 0), 0) / reviews.length).toFixed(1)
    : "—";
  const featuredCount = reviews.filter((r) => r.review?.featured).length;
  const reviewsReceived = reviews.length;
  const reviewsRequested = requests.filter((r) => r.status === "completed" && (r.review_requested || r.review)).length;
  const reviewConversion = reviewsRequested ? Math.round((reviewsReceived / reviewsRequested) * 100) : 0;

  const loadSubs = useCallback(async () => {
    try { const r = await api.get("/admin/subcontractors"); setSubs(r.data); } catch { /* noop */ }
  }, []);
  useEffect(() => { loadSubs(); }, [loadSubs]);

  const assignSub = async () => {
    if (!assignSubId) { toast.error("Pick a subcontractor"); return; }
    try {
      const res = await api.post(`/admin/requests/${selected.id}/assign-subcontractor`, { subcontractor_id: assignSubId, pay_amount: parseFloat(assignPay) || 0 });
      setSelected(res.data);
      setAssignSubId(""); setAssignPay("");
      loadSubs(); load();
      toast.success("Subcontractor assigned");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to assign");
    }
  };
  const unassignSub = async (sid) => {
    try {
      const res = await api.delete(`/admin/requests/${selected.id}/subcontractors/${sid}`);
      setSelected(res.data);
      loadSubs(); load();
    } catch (e) {
      toast.error("Failed to remove");
    }
  };

  const parseDate = (s) => {
    const [y, m, d] = s.split("-").map(Number);
    return new Date(y, m - 1, d);
  };
  const fmt = (d) =>
    `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  const scheduledDates = scheduledJobs.map((j) => parseDate(j.scheduled_date));
  const calendarJobs = scheduledJobs.map((r) => ({ ...r, id: r.id, date: r.scheduled_date, title: `${r.ref} · ${r.name}`, thumb: (r.completion_photos && r.completion_photos[0]) ? `${API}/files/${r.completion_photos[0]}` : null }));
  const filteredCalendarJobs = calStatusFilter === "all" ? calendarJobs : calendarJobs.filter((j) => j.status === calStatusFilter);

  const googleCalUrl = (r) => {
    if (!r?.scheduled_date) return null;
    const [y, m, d] = r.scheduled_date.split("-").map(Number);
    const start = `${y}${String(m).padStart(2, "0")}${String(d).padStart(2, "0")}`;
    const nd = new Date(y, m - 1, d + 1);
    const end = `${nd.getFullYear()}${String(nd.getMonth() + 1).padStart(2, "0")}${String(nd.getDate()).padStart(2, "0")}`;
    const title = `MCC Job ${r.ref} — ${r.name}`;
    const details = [r.categories?.join(", "), r.phone ? `Phone: ${r.phone}` : "", r.email ? `Email: ${r.email}` : ""].filter(Boolean).join("\n");
    return `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title)}&dates=${start}/${end}&details=${encodeURIComponent(details)}&location=${encodeURIComponent(r.address || "")}`;
  };
  const dayJobs = selectedDay
    ? scheduledJobs.filter((j) => j.scheduled_date === fmt(selectedDay))
    : [];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Top bar */}
      <header className="bg-navy-900 text-white sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <span className="w-9 h-9 rounded-sm bg-gold-600 flex items-center justify-center">
              <HammerIcon size={20} weight="fill" className="text-white" />
            </span>
            <span className="font-heading font-extrabold text-lg">MCC Service Corp. Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <span className="text-sm text-slate-400 hidden sm:inline">{user?.email}</span>
            <button
              data-testid="logout-btn"
              onClick={doLogout}
              className="inline-flex items-center gap-2 text-sm font-semibold rounded-sm border border-slate-700 hover:border-gold-600 px-4 py-2 transition-colors"
            >
              <SignOutIcon size={18} /> Logout
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-10">
        <h1 className="font-heading text-3xl font-bold text-slate-900 mb-1">Service Requests</h1>
        <p className="text-slate-500 mb-4">Manage incoming jobs and update their status.</p>

        <div className="flex items-center gap-2 mb-8" data-testid="timezone-setting">
          <GlobeIcon size={18} className="text-gold-600" />
          <span className="text-sm text-slate-500">Reminder timezone:</span>
          <div className="w-56">
            <Select value={timezone} onValueChange={changeTimezone}>
              <SelectTrigger data-testid="timezone-select" className="bg-white border-slate-300 h-9">
                <SelectValue placeholder="Select timezone" />
              </SelectTrigger>
              <SelectContent>
                {tzList.map((tz) => <SelectItem key={tz} value={tz}>{tz.replace("_", " ")}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-10">
          {STAT_META.map((m) => (
            <div key={m.key} data-testid={`stat-${m.key}`} className="bg-white border border-slate-200 rounded-sm p-5">
              <m.icon size={22} weight="duotone" className={m.color} />
              <p className="font-heading text-3xl font-extrabold text-slate-900 mt-3">{stats[m.key] ?? 0}</p>
              <p className="text-xs uppercase tracking-wide text-slate-500 mt-1">{m.label}</p>
            </div>
          ))}
        </div>

        {/* Upcoming schedule */}
        {scheduledJobs.length > 0 && (
          <div data-testid="upcoming-schedule" className="bg-white border border-slate-200 rounded-sm p-5 mb-8">
            <p className="text-xs uppercase tracking-wide font-bold text-gold-600 mb-4 flex items-center gap-2">
              <CalendarCheckIcon size={16} weight="fill" /> Upcoming Schedule
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {scheduledJobs.map((r) => (
                <button
                  key={r.id}
                  data-testid={`schedule-item-${r.ref}`}
                  onClick={() => openDetail(r)}
                  className="text-left border border-slate-200 hover:border-gold-600 rounded-sm p-3 transition-colors"
                >
                  <p className="font-mono text-sm font-semibold text-gold-700">{r.scheduled_date}</p>
                  <p className="text-sm font-medium text-slate-900 truncate">{r.name}</p>
                  <p className="text-xs text-slate-500 truncate">{r.categories.join(", ")}</p>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="flex flex-wrap gap-4 mb-6">
          <div className="w-44">
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger data-testid="filter-status" className="bg-white border-slate-300">
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                {STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="w-44">
            <Select value={categoryFilter} onValueChange={setCategoryFilter}>
              <SelectTrigger data-testid="filter-category" className="bg-white border-slate-300">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All categories</SelectItem>
                {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="ml-auto flex items-center gap-1 bg-white border border-slate-300 rounded-sm p-1">
            <button
              data-testid="view-list"
              onClick={() => setView("list")}
              className={`flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-sm transition-colors ${view === "list" ? "bg-navy-900 text-white" : "text-slate-600 hover:text-slate-900"}`}
            >
              <RowsIcon size={16} /> List
            </button>
            <button
              data-testid="view-calendar"
              onClick={() => setView("calendar")}
              className={`flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-sm transition-colors ${view === "calendar" ? "bg-navy-900 text-white" : "text-slate-600 hover:text-slate-900"}`}
            >
              <CalendarDotsIcon size={16} /> Calendar
            </button>
            <button
              data-testid="view-ratings"
              onClick={() => setView("ratings")}
              className={`flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-sm transition-colors ${view === "ratings" ? "bg-navy-900 text-white" : "text-slate-600 hover:text-slate-900"}`}
            >
              <StarIcon size={16} weight={view === "ratings" ? "fill" : "regular"} /> Ratings
            </button>
            <button
              data-testid="view-subs"
              onClick={() => setView("subs")}
              className={`flex items-center gap-1.5 text-sm font-semibold px-3 py-1.5 rounded-sm transition-colors ${view === "subs" ? "bg-navy-900 text-white" : "text-slate-600 hover:text-slate-900"}`}
            >
              <UsersThreeIcon size={16} weight={view === "subs" ? "fill" : "regular"} /> Subs
            </button>
          </div>
        </div>

        {view === "subs" ? (
          <SubcontractorsPanel subs={subs} reloadSubs={loadSubs} />
        ) : view === "ratings" ? (
          <div data-testid="ratings-view">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
              <div className="bg-white border border-slate-200 rounded-sm p-5">
                <StarIcon size={22} weight="fill" className="text-gold-500" />
                <p className="font-heading text-3xl font-extrabold text-slate-900 mt-3">{avgRating}</p>
                <p className="text-xs uppercase tracking-wide text-slate-500 mt-1">Avg rating</p>
              </div>
              <div className="bg-white border border-slate-200 rounded-sm p-5">
                <TrayIcon size={22} weight="duotone" className="text-slate-900" />
                <p className="font-heading text-3xl font-extrabold text-slate-900 mt-3">{reviews.length}</p>
                <p className="text-xs uppercase tracking-wide text-slate-500 mt-1">Total reviews</p>
              </div>
              <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid="review-conversion-stat">
                <StarIcon size={22} weight="regular" className="text-gold-600" />
                <p className="font-heading text-3xl font-extrabold text-slate-900 mt-3">{reviewsReceived}<span className="text-lg text-slate-400">/{reviewsRequested}</span></p>
                <p className="text-xs uppercase tracking-wide text-slate-500 mt-1">Requested vs received</p>
                <p className="text-[11px] text-slate-400 mt-0.5">{reviewConversion}% conversion</p>
              </div>
              <div className="bg-white border border-slate-200 rounded-sm p-5">
                <CheckCircleIcon size={22} weight="duotone" className="text-green-600" />
                <p className="font-heading text-3xl font-extrabold text-slate-900 mt-3">{featuredCount}</p>
                <p className="text-xs uppercase tracking-wide text-slate-500 mt-1">Featured live</p>
              </div>
            </div>
            {reviews.length === 0 ? (
              <div className="bg-white border border-slate-200 rounded-sm p-10 text-center text-slate-400">
                No reviews yet.
              </div>
            ) : (
              <div className="space-y-3">
                {reviews.map((r) => (
                  <div key={r.id} data-testid={`review-item-${r.ref}`} className="bg-white border border-slate-200 rounded-sm p-5">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 flex-wrap">
                        <span className="font-mono text-sm font-semibold text-gold-700">{r.ref}</span>
                        <span className="font-medium text-slate-900">{r.name}</span>
                        <span className="flex gap-0.5">
                          {[1, 2, 3, 4, 5].map((n) => <StarIcon key={n} size={16} weight={n <= (r.review?.rating || 0) ? "fill" : "regular"} className="text-gold-500" />)}
                        </span>
                      </div>
                      {r.review?.comment && <p className="text-sm text-slate-600 mt-1.5">"{r.review.comment}"</p>}
                      <p className="text-xs text-slate-400 mt-1 capitalize">{r.categories?.join(", ")}</p>
                    </div>
                    <button
                      data-testid={`feature-toggle-${r.ref}`}
                      onClick={() => toggleFeature(r)}
                      className={`shrink-0 rounded-sm text-sm font-semibold px-4 py-2 transition-colors active:scale-95 ${r.review?.featured ? "bg-gold-600 text-white hover:bg-gold-700" : "border border-slate-300 text-slate-700 hover:border-gold-600"}`}
                    >
                      {r.review?.featured ? "★ Featured" : "Feature on site"}
                    </button>
                    </div>
                    <div className="mt-3 border-t border-slate-100 pt-3">
                      <Label className="text-xs font-semibold text-slate-500 mb-1 block">Public reply (shown under the review)</Label>
                      <div className="flex flex-col sm:flex-row gap-2">
                        <Textarea data-testid={`reply-input-${r.ref}`} value={replyDrafts[r.id] ?? r.review?.reply ?? ""} onChange={(e) => setReplyDrafts((d) => ({ ...d, [r.id]: e.target.value }))} placeholder="Thanks for the kind words!" className="bg-white border-slate-300 flex-1 min-h-[38px]" />
                        <button data-testid={`save-reply-${r.ref}`} onClick={() => saveReply(r)} className="shrink-0 rounded-sm bg-navy-900 hover:bg-navy-800 text-white text-sm font-semibold px-4 h-9 sm:self-end transition-colors active:scale-95">Save reply</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : view === "calendar" ? (
          <div data-testid="calendar-view">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              {[["all", "All"], ["scheduled", "Scheduled"], ["in-progress", "In progress"], ["completed", "Completed"]].map(([val, lbl]) => (
                <button
                  key={val}
                  data-testid={`cal-filter-${val}`}
                  onClick={() => setCalStatusFilter(val)}
                  className={`text-xs font-semibold px-3 py-1.5 rounded-sm transition-colors ${calStatusFilter === val ? "bg-navy-900 text-white" : "text-slate-600 border border-slate-200 hover:text-slate-900"}`}
                >
                  {lbl}
                </button>
              ))}
            </div>
            <MonthCalendar jobs={filteredCalendarJobs} onJobClick={(j) => openDetail(j)} draggable onReschedule={rescheduleJob} exportable />
            <div className="mt-3 flex flex-wrap items-center gap-4 text-xs text-slate-500">
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-gold-200" /> Scheduled</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-blue-200" /> In progress</span>
              <span className="flex items-center gap-1.5"><span className="w-3 h-3 rounded-sm bg-green-200" /> Completed</span>
              <span className="text-slate-400">· Click a job to open it, or drag it to another day to reschedule</span>
            </div>
          </div>
        ) : (
        <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
          <table className="w-full text-sm" data-testid="requests-table">
            <thead>
              <tr className="border-b border-slate-200 text-left text-xs uppercase tracking-wide text-slate-500">
                <th className="px-5 py-4 font-semibold">Ref</th>
                <th className="px-5 py-4 font-semibold">Customer</th>
                <th className="px-5 py-4 font-semibold">Property</th>
                <th className="px-5 py-4 font-semibold">Services</th>
                <th className="px-5 py-4 font-semibold">Urgency</th>
                <th className="px-5 py-4 font-semibold">Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-400">Loading…</td></tr>
              ) : requests.length === 0 ? (
                <tr><td colSpan={6} className="px-5 py-10 text-center text-slate-400">No requests found.</td></tr>
              ) : (
                requests.map((r) => (
                  <tr
                    key={r.id}
                    data-testid={`request-row-${r.ref}`}
                    onClick={() => openDetail(r)}
                    className="border-b border-slate-100 last:border-0 hover:bg-gold-50/40 cursor-pointer transition-colors"
                  >
                    <td className="px-5 py-4 font-mono font-semibold text-slate-900">{r.ref}</td>
                    <td className="px-5 py-4">
                      <p className="font-medium text-slate-900">{r.name}</p>
                      <p className="text-slate-500 text-xs">{r.phone}</p>
                    </td>
                    <td className="px-5 py-4 capitalize text-slate-700">{r.property_type}</td>
                    <td className="px-5 py-4 text-slate-700 max-w-[200px] truncate">{r.categories.join(", ")}</td>
                    <td className="px-5 py-4 capitalize text-slate-700">{r.urgency}</td>
                    <td className="px-5 py-4">
                      <span className={`px-2.5 py-1 rounded-sm text-xs font-bold uppercase ${STATUS_STYLE[r.status]}`}>{r.status}</span>
                      {r.scheduled_date && (
                        <p className="text-xs text-slate-500 mt-1 flex items-center gap-1"><CalendarBlankIcon size={12} /> {r.scheduled_date}</p>
                      )}
                      {r.reschedule_history && r.reschedule_history.length > 0 && (
                        <span data-testid={`rescheduled-tag-${r.ref}`} className="mt-1 flex w-fit items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase bg-blue-100 text-blue-700"><ArrowsClockwiseIcon size={10} weight="bold" /> Rescheduled</span>
                      )}
                      {r.status === "completed" && (
                        r.review ? (
                          <span data-testid={`review-badge-${r.ref}`} className="mt-1 flex w-fit items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase bg-green-100 text-green-800"><StarIcon size={10} weight="fill" /> Reviewed</span>
                        ) : r.review_requested ? (
                          <span data-testid={`review-badge-${r.ref}`} className="mt-1 flex w-fit items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase bg-gold-100 text-gold-800"><StarIcon size={10} /> Requested</span>
                        ) : (
                          <span data-testid={`review-badge-${r.ref}`} className="mt-1 flex w-fit items-center gap-1 px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase bg-amber-100 text-amber-800">Needs review</span>
                        )
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        )}
      </div>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg" data-testid="request-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading flex items-center gap-3">
                  <span className="font-mono text-gold-600">{selected.ref}</span>
                  <span className={`px-2.5 py-1 rounded-sm text-xs font-bold uppercase ${STATUS_STYLE[selected.status]}`}>{selected.status}</span>
                </DialogTitle>
                <DialogDescription>Review request details and update its status.</DialogDescription>
              </DialogHeader>

              <div className="space-y-4 text-sm">
                <div className="grid grid-cols-1 gap-2">
                  <p className="flex items-center gap-2 text-slate-700"><span className="font-semibold text-slate-900">{selected.name}</span></p>
                  <p className="flex items-center gap-2 text-slate-600"><PhoneIcon size={16} className="text-gold-600" /> {selected.phone}</p>
                  <p className="flex items-center gap-2 text-slate-600"><EnvelopeSimpleIcon size={16} className="text-gold-600" /> {selected.email}</p>
                  <p className="flex items-center gap-2 text-slate-600"><MapPinIcon size={16} className="text-gold-600" /> {selected.address}</p>
                </div>

                <div className="bg-slate-50 border border-slate-200 rounded-sm p-4 space-y-1">
                  <p><span className="text-slate-500">Property:</span> <span className="capitalize font-medium">{selected.property_type}</span></p>
                  <p><span className="text-slate-500">Services:</span> <span className="font-medium">{selected.categories.join(", ")}</span></p>
                  <p><span className="text-slate-500">Urgency:</span> <span className="capitalize font-medium">{selected.urgency}</span></p>
                  <p><span className="text-slate-500">Preferred date:</span> <span className="font-medium">{selected.preferred_date || "Not specified"}</span></p>
                </div>

                <div>
                  <p className="text-slate-500 mb-1">Description</p>
                  <p className="text-slate-800 bg-slate-50 border border-slate-200 rounded-sm p-3">{selected.description}</p>
                </div>

                {selected.photos && selected.photos.length > 0 && (
                  <div>
                    <p className="text-slate-500 mb-2 flex items-center gap-1.5"><ImageIcon size={16} className="text-gold-600" /> Photos</p>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid="detail-photos">
                      {selected.photos.map((p) => (
                        <a key={p} href={`${API}/files/${p}`} target="_blank" rel="noopener noreferrer">
                          <img src={`${API}/files/${p}`} alt="attachment" className="w-full aspect-square object-cover rounded-sm border border-slate-200 hover:border-gold-600 transition-colors" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <p className="text-slate-500 mb-2 flex items-center gap-1.5"><ImagesIcon size={16} className="text-green-600" /> Finished-job photos</p>
                  <label
                    data-testid="completion-upload-label"
                    className="flex items-center justify-center gap-2 border-2 border-dashed border-slate-300 hover:border-green-600 rounded-sm py-4 cursor-pointer transition-colors bg-white text-sm font-medium text-slate-700"
                  >
                    <UploadSimpleIcon size={18} className="text-green-600" />
                    {completionUploading ? "Uploading…" : "Upload before/after photos"}
                    <input
                      data-testid="completion-input"
                      type="file"
                      accept="image/*"
                      multiple
                      className="hidden"
                      disabled={completionUploading}
                      onChange={(e) => { uploadCompletion(e.target.files); e.target.value = ""; }}
                    />
                  </label>
                  {completionDraft.length > 0 && (
                    <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 gap-3" data-testid="completion-grid">
                      {completionDraft.map((p) => (
                        <div key={p} className="relative group aspect-square">
                          <img src={`${API}/files/${p}`} alt="finished" className="w-full h-full object-cover rounded-sm border border-green-200" />
                          <button
                            type="button"
                            data-testid="remove-completion-btn"
                            onClick={() => removeCompletion(p)}
                            className="absolute -top-2 -right-2 bg-navy-900 text-white rounded-full p-1 opacity-90 hover:bg-red-600 transition-colors"
                          >
                            <span className="block w-3.5 h-3.5 text-[10px] leading-none flex items-center justify-center">✕</span>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  {selected.photos && selected.photos.length > 0 && selected.completion_photos && selected.completion_photos.length > 0 && (
                    <div className="mt-3 space-y-2">
                      <Input
                        data-testid="showcase-caption-input"
                        value={showcaseCaption}
                        onChange={(e) => setShowcaseCaption(e.target.value)}
                        placeholder="Optional caption (e.g. Leaky pipe fully replaced)"
                        maxLength={80}
                        className="bg-white border-slate-300"
                      />
                      {selected.showcase_featured ? (
                        <div className="flex gap-2">
                          <button data-testid="save-showcase-btn" onClick={() => toggleShowcase(true)} className="flex-1 rounded-sm bg-gold-600 text-white hover:bg-gold-700 text-sm font-semibold px-4 py-2 transition-colors active:scale-95">★ Save caption</button>
                          <button data-testid="remove-showcase-btn" onClick={() => toggleShowcase(false)} className="rounded-sm border border-slate-300 text-slate-700 hover:border-red-500 hover:text-red-600 text-sm font-semibold px-4 py-2 transition-colors active:scale-95">Remove</button>
                        </div>
                      ) : (
                        <button data-testid="feature-showcase-btn" onClick={() => toggleShowcase(true)} className="w-full rounded-sm border border-slate-300 text-slate-700 hover:border-gold-600 text-sm font-semibold px-4 py-2 transition-colors active:scale-95">Feature before/after on homepage</button>
                      )}
                    </div>
                  )}
                </div>

                {selected.pending_photos && selected.pending_photos.length > 0 && (
                  <div data-testid="pending-photos-section">
                    <Label className="text-slate-900 font-medium mb-2 block">Photos pending review <span className="text-xs font-normal text-slate-400">(uploaded by subcontractors — approve to show the customer)</span></Label>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                      {selected.pending_photos.map((pp) => (
                        <div key={pp.path} data-testid="pending-photo-item" className="relative">
                          <img src={`${API}/files/${pp.path}`} alt="pending" className="w-full aspect-square object-cover rounded-sm border border-gold-300" />
                          <p className="text-[10px] text-slate-500 truncate mt-0.5">{pp.sub || "Sub"}</p>
                          <div className="flex gap-1 mt-1">
                            <button data-testid="approve-photo-btn" onClick={() => reviewPhoto(pp.path, "approve")} className="flex-1 rounded-sm bg-green-600 hover:bg-green-700 text-white text-[10px] font-bold uppercase py-1 transition-colors active:scale-95">Approve</button>
                            <button data-testid="reject-photo-btn" onClick={() => reviewPhoto(pp.path, "reject")} className="flex-1 rounded-sm border border-slate-300 text-slate-600 hover:border-red-500 hover:text-red-600 text-[10px] font-bold uppercase py-1 transition-colors active:scale-95">Reject</button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <Label className="text-slate-900 font-medium mb-2 block flex items-center gap-1.5"><CalendarBlankIcon size={16} className="text-gold-600" /> Scheduled date</Label>
                  <Input
                    data-testid="schedule-date-input"
                    type="date"
                    value={scheduleDraft}
                    onChange={(e) => setScheduleDraft(e.target.value)}
                    className="bg-white border-slate-300 max-w-xs"
                  />
                  {selected.scheduled_date && (
                    <a
                      data-testid="add-to-gcal-btn"
                      href={googleCalUrl(selected)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold text-gold-700 hover:text-gold-800"
                    >
                      <CalendarPlusIcon size={14} /> Add to Google Calendar
                    </a>
                  )}
                  {selected.reschedule_history && selected.reschedule_history.length > 0 && (
                    <div data-testid="reschedule-history" className="mt-3 rounded-sm bg-slate-50 border border-slate-200 p-3">
                      <p className="text-[11px] font-bold uppercase tracking-wide text-slate-500 mb-2">Reschedule history</p>
                      <ul className="space-y-1.5">
                        {[...selected.reschedule_history].reverse().map((h, k) => (
                          <li key={k} data-testid={`reschedule-entry-${k}`} className="text-xs text-slate-600">
                            <span className="font-mono"><s className="text-slate-400">{h.from}</s> → <b className="text-slate-800">{h.to}</b></span>
                            {h.reason && <span className="text-slate-500"> — “{h.reason}”</span>}
                            <span className="text-slate-400"> · {(h.at || "").slice(0, 10)}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>

                <div>
                  <Label className="text-slate-900 font-medium mb-2 block flex items-center gap-1.5"><CurrencyDollarIcon size={16} className="text-gold-600" /> Cost estimate</Label>
                  <Input
                    data-testid="estimate-input"
                    value={estimateDraft}
                    onChange={(e) => setEstimateDraft(e.target.value)}
                    placeholder="e.g. $250 – $400"
                    className="bg-white border-slate-300 max-w-xs"
                  />
                  <p className="text-xs text-slate-500 mt-1">Shown to the customer on their tracking page.</p>
                  {selected.quote_status && (
                    <span
                      data-testid="admin-quote-status"
                      className={`inline-block mt-2 px-2.5 py-1 rounded-sm text-xs font-bold uppercase ${
                        selected.quote_status === "accepted"
                          ? "bg-green-100 text-green-800"
                          : selected.quote_status === "declined"
                          ? "bg-red-100 text-red-800"
                          : "bg-slate-200 text-slate-700"
                      }`}
                    >
                      Quote {selected.quote_status}
                    </span>
                  )}
                </div>

                <div>
                  <Label className="text-slate-900 font-medium mb-2 block">Internal / customer note</Label>
                  <Textarea
                    data-testid="admin-note-input"
                    value={noteDraft}
                    onChange={(e) => setNoteDraft(e.target.value)}
                    placeholder="Add a note visible to the customer on their tracking page…"
                    className="bg-white border-slate-300"
                  />
                  <button
                    data-testid="save-note-btn"
                    onClick={saveDetails}
                    className="mt-2 text-sm font-semibold text-gold-700 hover:text-gold-800 transition-colors"
                  >
                    Save note & schedule
                  </button>
                </div>

                <div>
                  <Label className="text-slate-900 font-medium mb-2 block">Update status</Label>
                  <div className="flex flex-wrap gap-2">
                    {STATUSES.map((s) => (
                      <button
                        key={s}
                        data-testid={`set-status-${s}`}
                        onClick={() => updateStatus(s)}
                        className={`px-3 py-2 rounded-sm text-xs font-bold uppercase transition-colors ${
                          selected.status === s
                            ? "bg-navy-900 text-white"
                            : "border border-slate-300 text-slate-700 hover:border-gold-600"
                        }`}
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>

                <div data-testid="invoice-builder">
                  <Label className="text-slate-900 font-medium mb-2 block flex items-center gap-1.5"><ReceiptIcon size={16} className="text-gold-600" /> Invoice</Label>
                  <div className="space-y-2">
                    {invoiceItems.map((it, idx) => (
                      <div key={idx} className="flex gap-2 items-center">
                        <Input data-testid={`invoice-desc-${idx}`} placeholder="Description" value={it.description} onChange={(e) => updateItem(idx, "description", e.target.value)} className="bg-white border-slate-300 flex-1 h-9" />
                        <Input placeholder="Qty" type="number" value={it.qty} onChange={(e) => updateItem(idx, "qty", e.target.value)} className="bg-white border-slate-300 w-16 h-9" />
                        <Input placeholder="$" type="number" value={it.unit_price} onChange={(e) => updateItem(idx, "unit_price", e.target.value)} className="bg-white border-slate-300 w-24 h-9" />
                        <button type="button" onClick={() => removeItem(idx)} className="text-slate-400 hover:text-red-600 transition-colors"><TrashIcon size={18} /></button>
                      </div>
                    ))}
                  </div>
                  <button type="button" data-testid="invoice-add-line" onClick={addItem} className="mt-2 text-sm font-semibold text-gold-700 hover:text-gold-800 inline-flex items-center gap-1"><PlusIcon size={14} weight="bold" /> Add line</button>
                  <div className="flex items-center justify-between mt-3">
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-slate-500">Deposit %</span>
                      <Input data-testid="invoice-deposit-pct" type="number" value={depositPct} onChange={(e) => setDepositPct(e.target.value)} className="bg-white border-slate-300 w-20 h-9" />
                    </div>
                    <p className="font-heading font-bold text-slate-900" data-testid="invoice-total">Total: ${invoiceTotal.toFixed(2)}</p>
                  </div>
                  <div className="flex items-center gap-3 mt-2">
                    <button data-testid="save-invoice-btn" onClick={saveInvoice} className="rounded-sm bg-navy-900 text-white text-sm font-semibold px-4 py-2 hover:bg-navy-800 transition-colors">Save invoice</button>
                    {selected.invoice?.status && (
                      <span data-testid="admin-invoice-status" className={`px-2.5 py-1 rounded-sm text-xs font-bold uppercase ${selected.invoice.status === "paid" ? "bg-green-100 text-green-800" : selected.invoice.status === "deposit_paid" ? "bg-gold-100 text-gold-800" : "bg-slate-200 text-slate-700"}`}>{selected.invoice.status.replace("_", " ")}</span>
                    )}
                  </div>
                </div>

                <div data-testid="assign-subs-section">
                  <Label className="text-slate-900 font-medium mb-2 block flex items-center gap-1.5"><UsersThreeIcon size={16} className="text-gold-600" /> Subcontractors <span className="text-xs font-normal text-slate-400">(internal)</span></Label>
                  {(selected.subcontractors || []).length > 0 && (
                    <div className="space-y-2 mb-3">
                      {selected.subcontractors.map((a) => (
                        <div key={a.subcontractor_id} data-testid={`assigned-sub-${a.subcontractor_id}`} className="flex items-center justify-between bg-slate-50 border border-slate-200 rounded-sm px-3 py-2">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-slate-900 truncate">{a.company_name}</p>
                            <p className="text-xs text-slate-500">{a.contact_name || "—"}{a.ein ? ` · EIN ${a.ein}` : ""} · Pay ${Number(a.pay_amount || 0).toLocaleString()}</p>
                          </div>
                          <div className="flex items-center gap-2 shrink-0">
                            {a.response && a.response !== "pending" && (
                              <span className={`px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase ${a.response === "accepted" ? "bg-green-100 text-green-800" : "bg-red-100 text-red-700"}`}>{a.response}</span>
                            )}
                            {a.sub_done && <span className="px-2 py-0.5 rounded-sm text-[10px] font-bold uppercase bg-blue-100 text-blue-800">done</span>}
                            <button data-testid={`unassign-sub-${a.subcontractor_id}`} onClick={() => unassignSub(a.subcontractor_id)} className="text-slate-400 hover:text-red-600 transition-colors"><TrashIcon size={16} /></button>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  {subs.length === 0 ? (
                    <p className="text-xs text-slate-400">No subcontractors in the registry yet. Add them from the "Subs" tab.</p>
                  ) : (
                    <div className="flex flex-wrap items-end gap-2">
                      <div className="flex-1 min-w-[160px]">
                        <Select value={assignSubId} onValueChange={setAssignSubId}>
                          <SelectTrigger data-testid="assign-sub-select" className="bg-white border-slate-300 h-9"><SelectValue placeholder="Select subcontractor" /></SelectTrigger>
                          <SelectContent>
                            {subs.map((s) => <SelectItem key={s.id} value={s.id}>{s.company_name}{s.trade ? ` — ${s.trade}` : ""}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <Input data-testid="assign-sub-pay" type="number" value={assignPay} onChange={(e) => setAssignPay(e.target.value)} placeholder="Pay $" className="bg-white border-slate-300 w-24 h-9" />
                      <button data-testid="assign-sub-btn" onClick={assignSub} className="rounded-sm bg-navy-900 hover:bg-navy-800 text-white text-sm font-semibold px-4 h-9 transition-colors active:scale-95">Assign</button>
                    </div>
                  )}
                </div>

                {selected.status === "completed" && (
                  <div data-testid="review-nudge-section" className="border border-gold-200 bg-gold-50/50 rounded-sm p-4">
                    <Label className="text-slate-900 font-medium mb-1 block flex items-center gap-1.5"><StarIcon size={16} weight="fill" className="text-gold-500" /> Review request</Label>
                    {selected.review ? (
                      <p className="text-xs text-green-700 font-semibold">Customer already left a review — nice work!</p>
                    ) : (
                      <>
                        <p className="text-xs text-slate-500 mb-2">{selected.review_requested ? "A review request was already sent. You can send another nudge." : "Email & text the customer a one-tap link to leave a public review."}</p>
                        <button
                          data-testid="send-review-request-btn"
                          onClick={sendReviewRequest}
                          className="rounded-sm bg-gold-600 hover:bg-gold-700 text-white text-sm font-semibold px-4 py-2 transition-colors active:scale-95"
                        >
                          {selected.review_requested ? "Send another nudge" : "Send review request"}
                        </button>
                      </>
                    )}
                  </div>
                )}

                {selected.review && (
                  <div data-testid="admin-review">
                    <Label className="text-slate-900 font-medium mb-1 block flex items-center gap-1.5"><StarIcon size={16} weight="fill" className="text-gold-500" /> Customer review</Label>
                    <div className="flex gap-0.5">
                      {[1, 2, 3, 4, 5].map((n) => <StarIcon key={n} size={18} weight={n <= selected.review.rating ? "fill" : "regular"} className="text-gold-500" />)}
                    </div>
                    {selected.review.comment && <p className="text-sm text-slate-600 mt-1">{selected.review.comment}</p>}
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
